/**
 * Component: Service plan dealer form
 * ------------------------------------------------------------------------------
 * Form functionality for service plans dealer page.
 *
 * @namespace spForm
 */
import {on} from '../helpers/utils'
import cssClasses from '../helpers/cssClasses'

import log from 'salvo-lite/log'
import axios from 'axios'
import zenscroll from 'zenscroll'
// import { values } from 'lodash'

class ServicePlansDealerForm {
    constructor (theme, elem) {
        this._elem = elem
        this._theme = theme
        this._settings = {

            ...JSON.parse(elem.getAttribute('data-service-plans-dealer-form') || '{}')
        }

        this._formData = {
            employeeName: '',
            employeeFNICode: '',
            employeeRISCReference: '',
            customerMobileNumber: '',
            customerEmailAddress: ''
        }

        this._formValidation = {
            employee: {
                employeeName: {
                    required: true
                },
                employeeFNICode: {
                    required: true
                },
                employeeRISCReference: {
                    required: true
                }
            },
            customer: {
                customerMobileNumber: {
                    required: false
                },
                customerEmailAddress: {
                    required: false
                }
            }
        }

        this._selectors = {
            label: '[data-spd="label"]',
            formGroup: '[data-spd="form-group"]',
            toggle: '[data-spd="toggle"]',
            inputs: '[data-spd="input"]',
            updateCustomerButton: '[data-spd="updateCustomer"]',
            updateEmployeeButton: '[data-spd="updateEmployee"]',
            continueButton: '[data-spd="continue"]',
            serviceForm: '[data-service-plans-form]',
            resetForm: '[data-spd="resetForm"]',
            resetFormAndVehicle: '[data-spd="resetFormAndVehicle"]',
            // Submitted personal details
            planName: '[data-spd="planName"]',
            name: '[data-spd="name"]',
            mobile: '[data-spd="mobile"]',
            email: '[data-spd="email"]',
            address: '[data-spd="address"]',
            currentMileage: '[data-spd="currentMileage"]',
            // Submitted plan details
            years: '[data-spd="years"]',
            annualMileage: '[data-spd="annualMileage"]',
            planTypes: '[data-spd="planType"]',
            confirmation: '[js-sp-form="confirmation"]',
            confirmation2: '[js-sp-form="confirmation2"]',
            confirmation3: '[js-sp-form="confirmation3"]'
            // customerSummary: '[data-spd="customerSummary"]'
        }

        this._nodeSelectors = {
            formGroup: this._elem.querySelectorAll(this._selectors.formGroup),
            toggle: this._elem.querySelectorAll(this._selectors.toggle),
            inputs: this._elem.querySelectorAll(this._selectors.inputs),
            updateCustomerButton: this._elem.querySelector(this._selectors.updateCustomerButton),
            updateEmployeeButton: this._elem.querySelector(this._selectors.updateEmployeeButton),
            continueButton: this._elem.querySelector(this._selectors.continueButton),
            serviceForm: document.querySelector(this._selectors.serviceForm),
            resetForm: this._elem.querySelectorAll(this._selectors.resetForm),
            resetFormAndVehicle: this._elem.querySelectorAll(this._selectors.resetFormAndVehicle),
            // Submitted personal details
            planName: this._elem.querySelectorAll(this._selectors.planName),
            name: this._elem.querySelectorAll(this._selectors.name),
            mobile: this._elem.querySelectorAll(this._selectors.mobile),
            email: this._elem.querySelectorAll(this._selectors.email),
            address: this._elem.querySelectorAll(this._selectors.address),
            currentMileage: this._elem.querySelectorAll(this._selectors.currentMileage),
            // Submitted plan details
            years: this._elem.querySelectorAll(this._selectors.years),
            annualMileage: this._elem.querySelector(this._selectors.annualMileage),
            planTypes: document.querySelectorAll(this._selectors.planTypes),
            confirmation: this._elem.querySelector(this._settings.confirmation),
            confirmation2: this._elem.querySelector(this._settings.confirmation2),
            confirmation3: this._elem.querySelector(this._settings.confirmation3)
            // customerSummary: this._elem.querySelector(this._selectors.customerSummary)
        }

        this._baacEndpoint = this._elem.dataset.baacEndpoint

        log.debug(this.constructor.name, 'Constructed', this)
    }

    /**
     * Initialise component.
     */
    onInit () {
        this._setEventListeners()
    }

    /**
     * Set event listeners.
    */
    _setEventListeners () {
        on('click', this._nodeSelectors.continueButton, () => this._handleContinue())
        on('click', this._nodeSelectors.updateCustomerButton, (e) => this._handleUpdate(e, 'customer'))
        on('click', this._nodeSelectors.updateEmployeeButton, (e) => this._handleUpdate(e, 'employee'))

        this._nodeSelectors.inputs.forEach((input) => on('keyup', input, (e) => this._handleInputValidation(e)))
        this._nodeSelectors.toggle.forEach((element) => on('click', element, (e) => this._toggleFormCollapse(e)))
        this._nodeSelectors.resetForm.forEach((button) => on('click', button, (e) => this._resetForm()))
        this._nodeSelectors.resetFormAndVehicle.forEach((button) => on('click', button, (e) => this._resetForm(true)))
        // this._nodeSelectors.planTypes.forEach(element => on('click', element, e => this._changeCheckboxes(e)))
        window.Frame.EventBus.listen('ServicePlanDealer:submit', (response) => this._handleFormSubmission(response))
        window.Frame.EventBus.listen('ServicePlan:hasPlan', (response) => this._handleNoPlans(response))
    }

    /**
     * Handle continue button click.
     */
    _handleContinue () {
        this._elem.classList.add('complete')
        this._nodeSelectors.formGroup.forEach((element) => element.classList.add(cssClasses.collapsed))
        this._nodeSelectors.serviceForm.classList.remove(cssClasses.hidden)
    }

    /**
     * Handle form update.
     * @param {Event} e
     */
    _handleUpdate (e, form) {
        let valid = false

        if (form === 'customer') {
            valid = this._handleCustomerValidation()
        }

        if (form === 'employee') {
            valid = this._handleEmployeeValidation()
        }

        if (!valid) return

        this._toggleFormCollapse(e)
    }

    /**
     * Toggle form between open & closed.
    */
    _toggleFormCollapse (e) {
        const formGroup = e.target.closest(this._selectors.formGroup)
        const updateEmployeeButton = formGroup.querySelector(this._selectors.updateEmployeeButton)
        const updateCustomerButton = formGroup.querySelector(this._selectors.updateCustomerButton)

        let allowToggle = false

        // Only allow toggle if the form group fields are valid.
        if ((updateEmployeeButton && this._handleEmployeeValidation()) || (updateCustomerButton && this._handleCustomerValidation())) {
            allowToggle = true
        }

        if (allowToggle) {
            formGroup.classList.toggle(cssClasses.collapsed)
        }
    }

    /**
     * Handle input validation and update formData on keyup.
     * @param {Event} e
     */
    _handleInputValidation (e) {
        const input = e.target
        const key = input.getAttribute('name')
        const value = input.value
        const label = input.closest(this._selectors.label)

        let inputValid = false

        this._formData[key] = value

        switch (key) {
        case 'customerMobileNumber':
            inputValid = this._validateTelephone(value)
            break

        case 'customerEmailAddress':
            inputValid = this._validateEmail(value)
            break

        case 'employeeName':
            inputValid = this._validateNotEmpty(value)
            break

        case 'employeeFNICode':
            inputValid = this._validateNotEmpty(value)
            break

        case 'employeeRISCReference':
            inputValid = this._validateRISCRef(value)
            break
        }

        if (inputValid) {
            label.classList.add(cssClasses.valid)
            label.classList.remove(cssClasses.erroneous)
        } else if (value === '') {
            label.classList.remove(cssClasses.valid)
            label.classList.remove(cssClasses.erroneous)
        } else {
            label.classList.remove(cssClasses.valid)
            label.classList.add(cssClasses.erroneous)
        }

        if (key.includes('customer')) {
            const customerFormValid = this._handleCustomerValidation(false)
            const updateButton = this._nodeSelectors.updateCustomerButton

            if (customerFormValid) {
                updateButton.removeAttribute('disabled')
            } else {
                updateButton.setAttribute('disabled', true)
            }
        }

        if (key.includes('employee')) {
            const employeeFormValid = this._handleEmployeeValidation(false)
            const updateEmployeeButton = this._nodeSelectors.updateEmployeeButton

            if (employeeFormValid) {
                updateEmployeeButton.removeAttribute('disabled')
            } else {
                updateEmployeeButton.setAttribute('disabled', true)
            }
        }

        const employeeValid = this._handleEmployeeValidation(false)
        const customerValid = this._handleCustomerValidation(false)

        if (employeeValid && customerValid) {
            this._nodeSelectors.continueButton.removeAttribute('disabled')
        } else {
            this._nodeSelectors.continueButton.setAttribute('disabled', true)
        }
    }

    /**
     * Validate form and handle error messaging.
     */
    _validateForm () {
        let employeeValid = this._handleEmployeeValidation()
        let customerValid = this._handleCustomerValidation()

        if (employeeValid && customerValid) {
            return true
        }

        return false
    }

    /**
     * Check atleast one customer field is complete.
     * @param {Boolean} allowStateUpdate Set to false to simply see whether form is valid or not without updating UI.
     */
    _handleCustomerValidation (allowStateUpdate = true) {
        let formValid = false

        // Check atleast one field is complete.
        Object.keys(this._formValidation.customer).forEach((key) => {
            const input = this._elem.querySelector(`input[name="${key}"]`)
            const label = input.closest(this._selectors.label)
            const value = input.value
            let inputValid = false

            this._formData[key] = value

            label.classList.remove(cssClasses.erroneous)

            switch (key) {
            case 'customerMobileNumber':
                inputValid = this._validateTelephone(value)
                break

            case 'customerEmailAddress':
                inputValid = this._validateEmail(value)
                break
            }

            if (inputValid === true) {
                formValid = true
            }
            if (value) {
                if (inputValid === true) {
                    label.classList.add(cssClasses.valid)
                    label.classList.remove(cssClasses.erroneous)
                } else {
                    label.classList.remove(cssClasses.valid)
                    label.classList.add(cssClasses.erroneous)
                }
            }
        })

        // If all fields are empty then add erroneous state to all
        if (!formValid && allowStateUpdate === true) {
            Object.keys(this._formValidation.customer).forEach((key) => {
                const input = this._elem.querySelector(`input[name="${key}"]`)
                const label = input.closest(this._selectors.label)

                label.classList.add(cssClasses.erroneous)
            })
        }

        return formValid
    }

    /**
     * Handle employee validation and ensure all fields are complete.
     * @param {Boolean} allowStateUpdate Set to false to simply see whether form is valid or not without updating UI.
     */
    _handleEmployeeValidation (allowStateUpdate = true) {
        let formValid = true

        Object.keys(this._formValidation.employee).forEach((key) => {
            const input = this._elem.querySelector(`input[name="${key}"]`)
            const label = input.closest(this._selectors.label)
            const value = input.value

            this._formData[key] = value

            let inputValid = false

            switch (key) {
            case 'employeeName':
                inputValid = this._validateNotEmpty(value)
                break

            // Now called Sales Exec DSLID
            case 'employeeFNICode':
                inputValid = this._validateNotEmpty(value)
                break

            // Now called Ford Dealer Code
            case 'employeeRISCReference':
                inputValid = this._validateRISCRef(value)
                break
            }

            if (!inputValid) {
                formValid = false
            }

            if (allowStateUpdate === true) {
                if (inputValid) {
                    label.classList.add(cssClasses.valid)
                    label.classList.remove(cssClasses.erroneous)
                } else {
                    label.classList.add(cssClasses.erroneous)
                    label.classList.remove(cssClasses.valid)
                }
            }
        })

        return formValid
    }

    /**
     * Submit form data to API.
     * @param {Object} servicePlanForm // Service Plan Data
     */
    _handleFormSubmission (servicePlanForm) {
        const data = {
            customer: {
                mobileNumber: this._formData.customerMobileNumber,
                emailAddress: this._formData.customerEmailAddress
            },
            employee: {
                name: this._formData.employeeName,
                FNICode: this._formData.employeeFNICode,
                RISCReference: this._formData.employeeRISCReference
            },
            servicePlanForm
        }

        // merge response with data and submit to API

        // Then show success screen
        this._nodeSelectors.annualMileage.innerText = data.servicePlanForm.distance

        this._nodeSelectors.planName.forEach((element) => {
            const planName = data.servicePlanForm.planName
            if (planName) {
                switch (planName) {
                case 'wearAndTear':
                    element.innerHTML = window.theme.strings.servicePlansDealer.planName.wearAndTear
                    break
                case 'servicePlan':
                    element.innerHTML = window.theme.strings.servicePlansDealer.planName.servicePlan
                    break
                case 'serviceBundle':
                    element.innerHTML = window.theme.strings.servicePlansDealer.planName.serviceBundle
                    break
                }
            } else if (data.servicePlanForm.serviceBundle === 'Yes') {
                element.innerHTML = window.theme.strings.servicePlansDealer.planName.serviceBundle
            } else if (data.servicePlanForm.servicePlan === 'Yes') {
                element.innerHTML = window.theme.strings.servicePlansDealer.planName.servicePlan
            } else if (data.servicePlanForm.wearAndTear === 'Yes') {
                element.innerHTML = window.theme.strings.servicePlansDealer.planName.wearAndTear
            }
        })

        this._nodeSelectors.years.forEach((element) => {
            element.innerHTML = data.servicePlanForm.years
        })

        this._nodeSelectors.name.forEach((element) => {
            element.innerHTML = `${data.servicePlanForm.firstName} ${data.servicePlanForm.middleInitial} ${data.servicePlanForm.lastName}`
        })

        this._nodeSelectors.mobile.forEach((element) => {
            // Optional field - if input not provided this hides the title:
            if (!data.customer.mobileNumber) {
                const header = element.previousElementSibling
                if (header) {
                    header.classList.add('visually-hidden')
                }
            }
            element.innerHTML = data.customer.mobileNumber
        })
        this._nodeSelectors.email.forEach((element) => {
            // Optional field - if input not provided this hides the title:
            if (!data.customer.emailAddress) {
                const header = element.previousElementSibling
                if (header) {
                    header.classList.add('visually-hidden')
                }
            }
            element.innerHTML = data.customer.emailAddress
        })
        this._nodeSelectors.address.forEach((element) => {
            // Optional fields
            const address2 = data.servicePlanForm.address2 ? `${data.servicePlanForm.address2} <br />` : ''

            element.innerHTML = `${data.servicePlanForm.address1}<br />${address2} ${data.servicePlanForm.city}<br />${data.servicePlanForm.zip}<br />${data.servicePlanForm.country}<br />`
        })

        this._nodeSelectors.currentMileage.forEach((element) => {
            element.innerHTML = data.servicePlanForm.current
        })

        this._elem.classList.add('submitted')
        this._elem.classList.remove('complete')
        this._nodeSelectors.serviceForm.classList.add(cssClasses.hidden)
        document.querySelector('[data-spd="dealerSubmit"]').addEventListener('click', e => {
            this._sendForm(data)
        })
    }
    _changeCheckboxes (e) {
        const checkbox1 = document.querySelector('[for="SP1Confirmation"]')
        const checkbox2 = document.querySelector('[for="SP1Confirmation2"]')
        const checkbox3 = document.querySelector('[for="SP1Confirmation3"]')
        switch (e.target.id) {
        case 'SP1WearAndTear':
        case 'SP1ServicePlan':
            checkbox2.classList.add(cssClasses.hidden)
            checkbox1.classList.remove(cssClasses.hidden)
            checkbox1.querySelector('input').checked = false
            checkbox2.querySelector('input').checked = true
            checkbox3.querySelector('input').checked = false
            break
        case 'SP1ServiceBundle':
            checkbox2.classList.remove(cssClasses.hidden)
            checkbox1.classList.add(cssClasses.hidden)
            checkbox1.querySelector('input').checked = false
            checkbox2.querySelector('input').checked = false
            checkbox3.querySelector('input').checked = true
            break
        }
    }
    _sendForm (data) {
        this._sendBAACRequest(data)
    }
    _sendBAACRequest (data) {
        const firstNameInitial = data.servicePlanForm.firstName.charAt(0)
        const yearTrimmed = data.servicePlanForm.year.substr(0, 4)
        const randomID = this._makeRandomID()
        const dealerEmailLink = data.servicePlanForm.dealerLink ? 1 : 0
        const dealerEmail = data.servicePlanForm.dealerEmail ? data.servicePlanForm.dealerEmail : null

        let payload = {
            // plan info
            'premium_amount': `${data.servicePlanForm.monthlyCost}`,
            'scheme_code': `${data.servicePlanForm.schemeCode}`,
            'quote_number': `${data.servicePlanForm.vinNumber}${randomID}`,

            // customer info
            'initials': `${firstNameInitial}`,
            'surname': `${data.servicePlanForm.lastName}`,
            'first_name': `${data.servicePlanForm.firstName}`,
            'cell_no': `${data.customer.mobileNumber}`,
            'email_address': `${data.customer.emailAddress}`,
            // not needed anymore:
            // 'work_number': '27312627198',

            // employee info
            'fni_name': `${data.employee.name}`,
            'fni_code': `FordDealer`,
            'risc_external_ref': `${data.employee.RISCReference}`,

            // customer address
            'phys_street1': `${data.servicePlanForm.address1}`,
            'phys_suburb': `${data.servicePlanForm.address2}`,
            'phys_town': `${data.servicePlanForm.city}`,
            'phys_code': `${data.servicePlanForm.zip}`,

            // vehicle details
            'chasis_no': `${data.servicePlanForm.vinNumber}`,
            'year': `${yearTrimmed}`,
            'kilometer': `${data.servicePlanForm.currentMileage}`,

            // dealer link
            'dealer_emaillink': parseInt(dealerEmailLink),
            'dealer_email': dealerEmail ? `${dealerEmail}` : null
        }
        axios.post(this._baacEndpoint, payload)
            .then((response) => {
                if (response.data.message === 'success') {
                    alert(window.theme.strings.servicePlansDealer.form.success)
                    this._resetForm(true)
                    this._resetForm()
                } else {
                    alert('There was an error with your submission\n' + response.data.errors.baac_exception)
                }
            })
    }

    _makeRandomID () {
        let text = ''
        const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

        for (var i = 0; i < 2; i++) { text += possible.charAt(Math.floor(Math.random() * possible.length)) }

        return text
    }

    /**
     * Returns true for valid email string else false.
     * @param {String} value
     */
    _validateEmail (value) {
        if (!value || value === '') return false

        // eslint-disable-next-line no-control-regex
        return /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(value)
    }

    /**
     * Returns true for a non-empty string else false.
     * @param {String} value
     */
    _validateNotEmpty (value) {
        if (!value || value === '') return false

        if (value.length > 0) {
            return true
        }

        return false
    }

    _validateRISCRef (value) {
        if (value.length > 7) return false
        return /^[0-9]{5}[a-zA-Z]{2}$/.test(value)
    }

    /**
     * Returns true for valid telephone number string else false.
     * @param {String} value
     */
    _validateTelephone (value) {
        if (!value || value === '') return false
        // eslint-disable-next-line no-useless-escape
        const valueNoSpace = value.replace(/\D/g, '')
        return /^(?:(?:00)?44)7(?:[45789]\d{2}|624)\d{6}$/.test(valueNoSpace)
    }

    /**
     * Show "No Plans" screen if no plans are available.
     * @param {Boolean} hasPlans
     */
    _handleNoPlans (hasPlans) {
        if (!hasPlans) {
            this._elem.classList.add('no-plans')
            this._nodeSelectors.serviceForm.classList.add(cssClasses.hidden)
            this._scrollTo('.sp-dealer')
        }
    }

    _scrollTo (elem) {
        let headerHeight = this._getHeaderHeight()
        let targetElement = document.querySelector(elem)
        let top = targetElement.getBoundingClientRect().top + zenscroll.getY() - headerHeight
        zenscroll.toY(top, 999)
    }

    _getHeaderHeight () {
        return this._theme.getSectionsByType('site-header')._currentHeight
    }

    /**
     * Reset Form.
     * @param {Boolean} clearVehicle
     */
    _resetForm (clearVehicle = false) {
        if (clearVehicle) {
            // Just reset selected vehicle
            this._elem.classList.remove('submitted')
            this._nodeSelectors.serviceForm.classList.remove('is-hidden')
            window._esc_GarageService.setActive(null)
        } else {
            // Resets whole plan

            // Reset classes
            this._elem.classList.remove('complete')
            this._nodeSelectors.serviceForm.classList.add(cssClasses.hidden)
            this._nodeSelectors.formGroup.forEach((element) => element.classList.remove(cssClasses.collapsed))

            // Reset form data
            Object.keys(this._formData).forEach((key) => {
                this._formData[key] = ''
            })

            this._nodeSelectors.inputs.forEach((input) => {
                const label = input.closest(this._selectors.label)
                input.value = ''
                label.classList.remove(cssClasses.valid)
            })
            // Reset service plans form
            window.Frame.EventBus.emit('ServicePlansForm:reset', true)
        }

        // Reset classes
        this._elem.classList.remove('no-plans')
        this._elem.classList.remove('submitted')

        // Reset Continue Button
        const updateEmployeeButton = this._nodeSelectors.updateEmployeeButton
        updateEmployeeButton.setAttribute('disabled', true)
        this._nodeSelectors.continueButton.setAttribute('disabled', true)
    }
    /**
     * Retrieve the required tag.
     * @param {Object} tags - The tags object.
     * @param {String} matchValue - The value to match.
     * @returns {String}
     */
    _retrieveTag (tags, matchValue) {
        let match = ''

        if (matchValue === 'Annual_Mileage') {
            match = []
        }

        tags.forEach((tag) => {
            if (tag.includes(matchValue)) {
                if (matchValue === 'Annual_Mileage') {
                    match.push(tag)
                } else {
                    match = tag
                }
            }
        })

        return match
    }
}

export default ServicePlansDealerForm



// WEBPACK FOOTER //
// ./src/resources/scripts/components/ServicePlansFormDealer.js